export type PlayerAudioTrack = {
  id?: number;
  name?: string;
  lang?: string;
  default?: boolean;
};

const LANGUAGE_NAMES = new Map<string, string>([
  ["en", "English"],
  ["eng", "English"],
  ["fr", "French"],
  ["fre", "French"],
  ["fra", "French"],
  ["es", "Spanish"],
  ["spa", "Spanish"],
  ["de", "German"],
  ["ger", "German"],
  ["deu", "German"],
  ["it", "Italian"],
  ["ita", "Italian"],
  ["ja", "Japanese"],
  ["jpn", "Japanese"],
  ["ko", "Korean"],
  ["kor", "Korean"],
  ["pt", "Portuguese"],
  ["por", "Portuguese"],
  ["zh", "Chinese"],
  ["chi", "Chinese"],
  ["zho", "Chinese"],
]);

const LANGUAGE_ALIASES = new Map<string, string>([
  ["eng", "en"],
  ["fre", "fr"],
  ["fra", "fr"],
  ["spa", "es"],
  ["ger", "de"],
  ["deu", "de"],
  ["ita", "it"],
  ["jpn", "ja"],
  ["kor", "ko"],
  ["por", "pt"],
  ["chi", "zh"],
  ["zho", "zh"],
]);

export function normalizeAudioLanguage(language: string | undefined): string {
  const normalized = (language ?? "").trim().toLowerCase().replace("_", "-");
  if (!normalized) {
    return "";
  }
  const baseLanguage = normalized.split("-")[0] ?? normalized;
  return LANGUAGE_ALIASES.get(baseLanguage) ?? baseLanguage;
}

export function formatAudioTrackLabel(track: PlayerAudioTrack, index: number): string {
  const language = normalizeAudioLanguage(track.lang);
  const languageName = LANGUAGE_NAMES.get(language) ?? (language ? language.toUpperCase() : "");
  const name = (track.name ?? "").trim();
  const suffix = track.default ? " (Default)" : "";

  if (languageName && name && !name.toLowerCase().includes(languageName.toLowerCase())) {
    return `${languageName} - ${name}${suffix}`;
  }
  if (name) {
    return `${name}${suffix}`;
  }
  if (languageName) {
    return `${languageName}${suffix}`;
  }
  return `Audio ${index + 1}${suffix}`;
}

export function preferredAudioLanguagesFromNavigator(navigatorLike: Pick<Navigator, "language" | "languages"> | undefined): string[] {
  const candidates = [
    ...(Array.isArray(navigatorLike?.languages) ? navigatorLike.languages : []),
    navigatorLike?.language,
  ];
  const normalized = candidates
    .map((language) => normalizeAudioLanguage(language))
    .filter((language): language is string => language.length > 0);
  return [...new Set(normalized)];
}

export function selectPreferredAudioTrackIndex(
  tracks: PlayerAudioTrack[],
  preferredLanguages: string[],
): number {
  if (tracks.length === 0) {
    return -1;
  }

  const normalizedPreferred = preferredLanguages
    .map((language) => normalizeAudioLanguage(language))
    .filter((language) => language.length > 0);
  for (const preferred of normalizedPreferred) {
    const index = tracks.findIndex((track) => normalizeAudioLanguage(track.lang) === preferred);
    if (index >= 0) {
      return index;
    }
  }

  const defaultIndex = tracks.findIndex((track) => track.default);
  if (defaultIndex >= 0) {
    return defaultIndex;
  }
  return 0;
}
